import React, { useRef, useMemo, useEffect, useState, useCallback } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, TransformControls } from '@react-three/drei';
import * as THREE from 'three';

// 16 Blockbench units = 1 Three.js unit (1 bloque Minecraft)
const MC_SCALE = 1 / 16;
const BASE_FILL = '#B0B0B0';
const PLATFORM_SIZE = 24; // 24x24 bloques = 3x3 módulos de 8x8

/* ============================================================================
 * Cubo individual - registra su mesh en el meshMap
 * ============================================================================ */
const ModelCube = React.memo(({ cube, texture, resolution, isSelected, onClick, onMeshRef }) => {
  const meshRef = useRef();

  useEffect(() => {
    if (meshRef.current && onMeshRef) {
      onMeshRef(cube.id, meshRef.current);
    }
    return () => {
      if (onMeshRef) onMeshRef(cube.id, null);
    };
  }, [cube.id, onMeshRef]);

  const centerPos = useMemo(() => [
    ((cube.from[0] + cube.to[0]) / 2) * MC_SCALE,
    ((cube.from[1] + cube.to[1]) / 2) * MC_SCALE,
    ((cube.from[2] + cube.to[2]) / 2) * MC_SCALE,
  ], [cube.from, cube.to]);

  const scaledSize = useMemo(() => [
    cube.size[0] * MC_SCALE,
    cube.size[1] * MC_SCALE,
    cube.size[2] * MC_SCALE,
  ], [cube.size]);

  const geometry = useMemo(() => {
    const geom = new THREE.BoxGeometry(scaledSize[0], scaledSize[1], scaledSize[2]);
    if (resolution && cube.faces) {
      const uvAttr = geom.attributes.uv;
      const { width: tw, height: th } = resolution;
      const faceMap = ['east', 'west', 'up', 'down', 'south', 'north'];
      faceMap.forEach((faceName, faceIdx) => {
        const face = cube.faces[faceName];
        if (face && face.uv) {
          const [u1, v1, u2, v2] = face.uv;
          const nu1 = u1 / tw;
          const nu2 = u2 / tw;
          const nv1 = 1 - v1 / th;
          const nv2 = 1 - v2 / th;
          const startIdx = faceIdx * 4;
          uvAttr.setXY(startIdx + 0, nu1, nv1);
          uvAttr.setXY(startIdx + 1, nu2, nv1);
          uvAttr.setXY(startIdx + 2, nu1, nv2);
          uvAttr.setXY(startIdx + 3, nu2, nv2);
        }
      });
      uvAttr.needsUpdate = true;
    }
    return geom;
  }, [scaledSize, cube.faces, resolution]);

  const material = useMemo(() => {
    if (texture) {
      const loader = new THREE.TextureLoader();
      const tex = loader.load(texture);
      tex.magFilter = THREE.NearestFilter;
      tex.minFilter = THREE.NearestFilter;
      // Interpretar la textura como sRGB — sin esto Three.js la trata como linear
      // y aplica una conversión que desatura los colores.
      tex.colorSpace = THREE.SRGBColorSpace;
      // Preservar transparencia real del modelo:
      // - transparent:true permite alpha del PNG
      // - alphaTest evita artefactos de orden de renderizado en zonas totalmente transparentes
      return new THREE.MeshStandardMaterial({
        map: tex,
        transparent: true,
        alphaTest: 0.05,
        side: THREE.DoubleSide,
        roughness: 0.85,
        metalness: 0,
      });
    }
    // Cubo sin textura (nuevo): color base opaco visible
    return new THREE.MeshStandardMaterial({
      color: BASE_FILL, roughness: 0.85, metalness: 0,
    });
  }, [texture]);

  const handleClick = (e) => {
    e.stopPropagation();
    onClick && onClick(cube.id, {
      ctrlKey: e.nativeEvent?.ctrlKey || e.nativeEvent?.metaKey,
      shiftKey: e.nativeEvent?.shiftKey,
    });
  };

  if (cube.visible === false) return null;

  // Wireframe sutil para que los cubos con zonas transparentes sigan siendo visibles
  const showSubtleOutline = !!texture && !isSelected;

  return (
    <group>
      <mesh
        ref={meshRef}
        position={centerPos}
        rotation={cube.rotation || [0, 0, 0]}
        geometry={geometry}
        material={material}
        onClick={handleClick}
        onPointerDown={(e) => e.stopPropagation()}
        userData={{ cubeId: cube.id }}
      />
      {showSubtleOutline && (
        <lineSegments position={centerPos} rotation={cube.rotation || [0, 0, 0]}>
          <edgesGeometry args={[new THREE.BoxGeometry(scaledSize[0] * 1.001, scaledSize[1] * 1.001, scaledSize[2] * 1.001)]} />
          <lineBasicMaterial color="#4A9EFF" transparent opacity={0.18} />
        </lineSegments>
      )}
      {isSelected && (
        <lineSegments position={centerPos} rotation={cube.rotation || [0, 0, 0]}>
          <edgesGeometry args={[new THREE.BoxGeometry(scaledSize[0] * 1.005, scaledSize[1] * 1.005, scaledSize[2] * 1.005)]} />
          <lineBasicMaterial color="#4A9EFF" depthTest={false} />
        </lineSegments>
      )}
    </group>
  );
});

/* ============================================================================
 * GizmoLayer - Gizmo bulletproof con Object3D estable
 *
 * Arquitectura:
 *  - Un THREE.Object3D() STABLE (creado una vez con useRef) actúa como proxy
 *  - Se añade a la escena via <primitive> (nunca se desmonta durante drag)
 *  - Antes del drag, se posiciona en el centroide de la selección
 *  - Durante drag: TransformControls modifica proxy.position/rotation/scale
 *  - En cada objectChange, calculamos el delta y APLICAMOS DIRECTAMENTE al mesh.matrixWorld
 *    de los cubos seleccionados (bypasseando el sistema de React position={})
 *  - En drag-end, commit único al state via callback
 *
 * Clave: Durante el drag NO se modifica el state React,
 * por lo tanto NO hay re-renders, por lo tanto los meshes no seleccionados NO se tocan.
 * ============================================================================ */
const GizmoLayer = ({
  selectedIds,
  meshMap,
  cubesById,
  mode,
  selectionCentroid,
  onCommit,
  onDragging,
  orbitRef,
}) => {
  // Proxy estable — creado UNA vez, nunca cambia
  const proxyRef = useRef();
  if (!proxyRef.current) {
    proxyRef.current = new THREE.Object3D();
    proxyRef.current.name = 'GizmoProxy';
  }
  const proxy = proxyRef.current;

  const controlsRef = useRef();
  const dragStateRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Cuando la selección cambia (y NO estamos arrastrando), posicionar el proxy
  useEffect(() => {
    if (isDraggingRef.current) return;
    if (!selectionCentroid) return;
    proxy.position.set(...selectionCentroid);
    proxy.rotation.set(0, 0, 0);
    proxy.scale.set(1, 1, 1);
    proxy.updateMatrixWorld(true);
  }, [selectionCentroid, proxy]);

  // Handlers estables via refs para evitar re-registrar listeners
  const currentSelectionRef = useRef(selectedIds);
  const currentMeshMapRef = useRef(meshMap);
  const currentCubesByIdRef = useRef(cubesById);
  const currentModeRef = useRef(mode);
  useEffect(() => {
    currentSelectionRef.current = selectedIds;
    currentMeshMapRef.current = meshMap;
    currentCubesByIdRef.current = cubesById;
    currentModeRef.current = mode;
  }, [selectedIds, meshMap, cubesById, mode]);

  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;

    const handleDraggingChanged = (event) => {
      isDraggingRef.current = event.value;
      if (orbitRef && orbitRef.current) {
        orbitRef.current.enabled = !event.value;
      }
      onDragging && onDragging(event.value);

      const ids = currentSelectionRef.current;
      const mMap = currentMeshMapRef.current;
      const cById = currentCubesByIdRef.current;

      if (event.value) {
        // ===== DRAG START =====
        const meshSnaps = {};
        ids.forEach((id) => {
          const m = mMap.get(id);
          const c = cById[id];
          if (m && c) {
            meshSnaps[id] = {
              meshPos: [m.position.x, m.position.y, m.position.z],
              meshRot: [m.rotation.x, m.rotation.y, m.rotation.z],
              meshScl: [m.scale.x, m.scale.y, m.scale.z],
              from: [...c.from],
              to: [...c.to],
              size: [...c.size],
              rotationDegrees: [...(c.rotationDegrees || [0, 0, 0])],
            };
          }
        });
        dragStateRef.current = {
          proxyStart: {
            position: [proxy.position.x, proxy.position.y, proxy.position.z],
            rotation: [proxy.rotation.x, proxy.rotation.y, proxy.rotation.z],
            scale: [proxy.scale.x, proxy.scale.y, proxy.scale.z],
          },
          meshes: meshSnaps,
        };
      } else {
        // ===== DRAG END =====
        if (!dragStateRef.current) return;
        const start = dragStateRef.current;
        const delta = {
          position: [
            proxy.position.x - start.proxyStart.position[0],
            proxy.position.y - start.proxyStart.position[1],
            proxy.position.z - start.proxyStart.position[2],
          ],
          rotation: [
            proxy.rotation.x - start.proxyStart.rotation[0],
            proxy.rotation.y - start.proxyStart.rotation[1],
            proxy.rotation.z - start.proxyStart.rotation[2],
          ],
          scale: [
            proxy.scale.x / (start.proxyStart.scale[0] || 1),
            proxy.scale.y / (start.proxyStart.scale[1] || 1),
            proxy.scale.z / (start.proxyStart.scale[2] || 1),
          ],
        };
        onCommit(delta, start, currentModeRef.current);
        dragStateRef.current = null;
      }
    };

    const handleObjectChange = () => {
      if (!dragStateRef.current) return;
      const start = dragStateRef.current;
      const ids = currentSelectionRef.current;
      const mMap = currentMeshMapRef.current;
      const modeNow = currentModeRef.current;

      const dpx = proxy.position.x - start.proxyStart.position[0];
      const dpy = proxy.position.y - start.proxyStart.position[1];
      const dpz = proxy.position.z - start.proxyStart.position[2];
      const drx = proxy.rotation.x - start.proxyStart.rotation[0];
      const dry = proxy.rotation.y - start.proxyStart.rotation[1];
      const drz = proxy.rotation.z - start.proxyStart.rotation[2];
      const dsx = proxy.scale.x / (start.proxyStart.scale[0] || 1);
      const dsy = proxy.scale.y / (start.proxyStart.scale[1] || 1);
      const dsz = proxy.scale.z / (start.proxyStart.scale[2] || 1);

      ids.forEach((id) => {
        const m = mMap.get(id);
        const snap = start.meshes[id];
        if (!m || !snap) return;

        if (modeNow === 'translate') {
          m.position.set(
            snap.meshPos[0] + dpx,
            snap.meshPos[1] + dpy,
            snap.meshPos[2] + dpz
          );
        } else if (modeNow === 'rotate') {
          m.rotation.set(
            snap.meshRot[0] + drx,
            snap.meshRot[1] + dry,
            snap.meshRot[2] + drz
          );
        } else if (modeNow === 'scale') {
          m.scale.set(
            snap.meshScl[0] * dsx,
            snap.meshScl[1] * dsy,
            snap.meshScl[2] * dsz
          );
        }
      });
    };

    controls.addEventListener('dragging-changed', handleDraggingChanged);
    controls.addEventListener('objectChange', handleObjectChange);
    return () => {
      controls.removeEventListener('dragging-changed', handleDraggingChanged);
      controls.removeEventListener('objectChange', handleObjectChange);
    };
  }, [proxy, onCommit, onDragging, orbitRef]);

  return (
    <>
      <primitive object={proxy} />
      <TransformControls
        ref={controlsRef}
        object={proxy}
        mode={mode}
        size={0.7}
        translationSnap={MC_SCALE}
        rotationSnap={Math.PI / 12}
        scaleSnap={0.1}
      />
    </>
  );
};

/* ============================================================================
 * Plataforma de referencia (8x8) con ejes N/S/E/O grabados en el propio suelo
 * ============================================================================ */
export const ReferencePlatform = () => {
  const size = PLATFORM_SIZE * MC_SCALE;

  const gridTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 1024;
    const ctx = canvas.getContext('2d');
    // Fondo muy sutil
    ctx.fillStyle = 'rgba(74, 158, 255, 0.05)';
    ctx.fillRect(0, 0, 1024, 1024);
    // Cuadrícula fina: cada uno de los 24 bloques (24 líneas)
    ctx.strokeStyle = 'rgba(74, 158, 255, 0.22)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 24; i++) {
      const p = (i / 24) * 1024;
      ctx.beginPath();
      ctx.moveTo(p, 0); ctx.lineTo(p, 1024);
      ctx.moveTo(0, p); ctx.lineTo(1024, p);
      ctx.stroke();
    }
    // Líneas de módulo 8x8 (cada 8 bloques - 4 líneas total dividiendo en 3x3)
    ctx.strokeStyle = 'rgba(74, 158, 255, 0.55)';
    ctx.lineWidth = 3;
    for (let i = 0; i <= 3; i++) {
      const p = (i / 3) * 1024;
      ctx.beginPath();
      ctx.moveTo(p, 0); ctx.lineTo(p, 1024);
      ctx.moveTo(0, p); ctx.lineTo(1024, p);
      ctx.stroke();
    }
    // Etiquetas N/S/E/O grabadas en el suelo — coord del módulo central
    ctx.fillStyle = 'rgba(74, 158, 255, 0.42)';
    ctx.font = 'bold 42px "Helvetica Neue", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('N', 512, 50);
    ctx.fillText('S', 512, 974);
    ctx.fillText('E', 974, 512);
    ctx.fillText('O', 50, 512);

    const tex = new THREE.CanvasTexture(canvas);
    tex.magFilter = THREE.LinearFilter;
    tex.minFilter = THREE.LinearFilter;
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }, []);

  return (
    <mesh position={[0, -0.001, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[size, size]} />
      <meshBasicMaterial
        map={gridTexture}
        transparent
        opacity={0.92}
        side={THREE.DoubleSide}
        depthWrite={false}
      />
    </mesh>
  );
};

/* ============================================================================
 * Renderer principal
 * ============================================================================ */
const ModelRenderer = ({
  model,
  selectedIds,
  onSelectCube,
  transformMode,
  onCubesTransform,
  orbitRef,
  onDraggingChange,
}) => {
  const meshMapRef = useRef(new Map());
  // Offset inicial del grupo (para centrar visualmente el modelo al cargar).
  // Se calcula UNA sola vez por proyecto — NO se recalcula al editar cubos,
  // para que el gizmo/mundo NO se desplacen mientras el usuario mueve piezas.
  const initialOffsetRef = useRef(null);
  // Contador de model.id/reference para saber cuándo es una carga NUEVA de proyecto
  // (no una edición). Detectamos "nueva carga" si el nº de cubos pasa de 0/undefined a algo.
  const prevCubeCountRef = useRef(0);

  const cubesById = useMemo(() => {
    const map = {};
    (model?.cubes || []).forEach((c) => { map[c.id] = c; });
    return map;
  }, [model?.cubes]);

  // Calcular offset SOLO en la carga inicial (cuando pasamos de 0→N cubos)
  // o cuando aún no había offset guardado. NUNCA recalcular durante ediciones.
  const offset = useMemo(() => {
    const cubes = model?.cubes || [];
    const prevCount = prevCubeCountRef.current;
    prevCubeCountRef.current = cubes.length;

    if (initialOffsetRef.current && prevCount > 0) {
      // Ya hay offset y no es la carga inicial — mantenerlo estable
      return initialOffsetRef.current;
    }

    if (cubes.length === 0) {
      const zero = [0, 0, 0];
      initialOffsetRef.current = zero;
      return zero;
    }

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxZ = -Infinity;
    cubes.forEach((cube) => {
      minX = Math.min(minX, cube.from[0]);
      minY = Math.min(minY, cube.from[1]);
      minZ = Math.min(minZ, cube.from[2]);
      maxX = Math.max(maxX, cube.to[0]);
      maxZ = Math.max(maxZ, cube.to[2]);
    });
    const centerX = (minX + maxX) / 2;
    const centerZ = (minZ + maxZ) / 2;
    const off = [-centerX * MC_SCALE, -minY * MC_SCALE, -centerZ * MC_SCALE];
    initialOffsetRef.current = off;
    return off;
  }, [model?.cubes]);

  // Centroide de la selección en coords LOCALES del grupo padre
  const selectionCentroid = useMemo(() => {
    if (!selectedIds || selectedIds.length === 0) return null;
    const selectedCubes = selectedIds
      .map((id) => cubesById[id])
      .filter(Boolean);
    if (selectedCubes.length === 0) return null;
    let cx = 0, cy = 0, cz = 0;
    selectedCubes.forEach((cube) => {
      cx += (cube.from[0] + cube.to[0]) / 2;
      cy += (cube.from[1] + cube.to[1]) / 2;
      cz += (cube.from[2] + cube.to[2]) / 2;
    });
    return [
      (cx / selectedCubes.length) * MC_SCALE,
      (cy / selectedCubes.length) * MC_SCALE,
      (cz / selectedCubes.length) * MC_SCALE,
    ];
  }, [selectedIds, cubesById]);

  const handleMeshRef = useCallback((cubeId, mesh) => {
    if (mesh) {
      meshMapRef.current.set(cubeId, mesh);
    } else {
      meshMapRef.current.delete(cubeId);
    }
  }, []);

  const handleCommit = useCallback((delta, startSnap, mode) => {
    onCubesTransform(selectedIds, delta, startSnap, mode);
  }, [selectedIds, onCubesTransform]);

  if (!model || !model.cubes) return null;

  const [offsetX, offsetY, offsetZ] = offset;

  const showGizmo = selectionCentroid && transformMode && transformMode !== 'select';

  return (
    <group position={[offsetX, offsetY, offsetZ]}>
      {model.cubes.map((cube) => (
        <ModelCube
          key={cube.id}
          cube={cube}
          texture={model.texture}
          resolution={model.resolution}
          isSelected={selectedIds?.includes(cube.id)}
          onClick={onSelectCube}
          onMeshRef={handleMeshRef}
        />
      ))}

      {showGizmo && (
        <GizmoLayer
          selectedIds={selectedIds}
          meshMap={meshMapRef.current}
          cubesById={cubesById}
          mode={transformMode}
          selectionCentroid={selectionCentroid}
          onCommit={handleCommit}
          onDragging={onDraggingChange}
          orbitRef={orbitRef}
        />
      )}
    </group>
  );
};

const CameraController = ({ controlsRef, orbitRef }) => {
  const { camera, gl, scene } = useThree();
  useEffect(() => {
    if (controlsRef) {
      controlsRef.current = {
        centerView: () => {
          camera.position.set(2.5, 2, 2.5);
          camera.lookAt(0, 0.5, 0);
          if (orbitRef.current) {
            orbitRef.current.target.set(0, 0.5, 0);
            orbitRef.current.update();
          }
        },
        // Captura el canvas 3D como PNG base64 (para pack_icon.png y thumbnails).
        // Requiere preserveDrawingBuffer:true en Canvas gl props.
        captureCover: () => {
          try {
            gl.render(scene, camera);
            return gl.domElement.toDataURL('image/png');
          } catch (e) {
            console.error('captureCover failed:', e);
            return null;
          }
        },
      };
    }
  }, [camera, controlsRef, orbitRef, gl, scene]);
  return null;
};

const ThreeViewer = ({
  model,
  selectedIds = [],
  onSelectCube,
  cameraControlsRef,
  transformMode = 'select',
  onCubesTransform,
}) => {
  const orbitRef = useRef();
  const [isDragging, setIsDragging] = useState(false);

  return (
    <div
      className="w-full h-full relative"
      style={{ background: '#1E1E23' }}
      data-testid="three-viewer"
    >
      <Canvas
        shadows
        onPointerMissed={() => onSelectCube && onSelectCube(null, {})}
        gl={{ antialias: true, alpha: false, preserveDrawingBuffer: true }}
      >
        <PerspectiveCamera makeDefault position={[2.5, 2, 2.5]} fov={45} />
        <ambientLight intensity={0.75} />
        <directionalLight position={[5, 10, 5]} intensity={0.7} castShadow />
        <directionalLight position={[-5, 5, -5]} intensity={0.3} />
        <directionalLight position={[0, -5, 3]} intensity={0.2} />

        <ReferencePlatform />

        <ModelRenderer
          model={model}
          selectedIds={selectedIds}
          onSelectCube={onSelectCube}
          transformMode={transformMode}
          onCubesTransform={onCubesTransform}
          orbitRef={orbitRef}
          onDraggingChange={setIsDragging}
        />

        <OrbitControls
          ref={orbitRef}
          enablePan
          enableZoom
          enableRotate
          minDistance={0.4}
          maxDistance={10}
          target={[0, 0.5, 0]}
          panSpeed={0.8}
          zoomSpeed={1.2}
          rotateSpeed={0.8}
          enabled={!isDragging}
        />

        <CameraController controlsRef={cameraControlsRef} orbitRef={orbitRef} />
      </Canvas>
    </div>
  );
};

export default ThreeViewer;
